import { Component, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';
import { ProjectManagementService } from '../project-management.service';

@Component({
  selector: 'app-project-overview',
  templateUrl: './project-overview.component.html',
  styleUrls: ['./project-overview.component.scss']
})
export class ProjectOverviewComponent implements OnInit {
  loading = false;
  projects: any[] = [];
  selectedProjectId: number | null = null;
  selectedProject: any = null;
  tasks: any[] = [];
  onboarding: any[] = [];

  constructor(private projectService: ProjectManagementService) {}

  ngOnInit(): void {
    this.loadProjects();
  }

  loadProjects(): void {
    this.loading = true;
    this.projectService.getProjects().subscribe({
      next: (projects) => {
        this.projects = projects || [];
        this.selectedProjectId = this.projects[0]?.id || null;
        this.loadOverview();
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load projects.', 'error');
      }
    });
  }

  loadOverview(): void {
    if (!this.selectedProjectId) {
      this.selectedProject = null;
      this.tasks = [];
      this.onboarding = [];
      this.loading = false;
      return;
    }

    this.loading = true;
    forkJoin({
      project: this.projectService.getProject(this.selectedProjectId),
      tasks: this.projectService.getProjectTasks(this.selectedProjectId),
      onboarding: this.projectService.getProjectOnboarding(this.selectedProjectId)
    }).subscribe({
      next: ({ project, tasks, onboarding }) => {
        this.selectedProject = project || null;
        this.tasks = tasks || [];
        this.onboarding = onboarding || [];
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        Swal.fire('Error', err?.error?.message || 'Failed to load project overview.', 'error');
      }
    });
  }

  get completedTasks(): number {
    return this.tasks.filter((task) => ['done', 'completed'].includes(String(task.status || '').toLowerCase())).length;
  }

  get overdueTasks(): number {
    const now = new Date();
    return this.tasks.filter((task) => {
      if (!task?.dueDate) return false;
      const due = new Date(task.dueDate);
      const done = ['done', 'completed'].includes(String(task.status || '').toLowerCase());
      return !done && !Number.isNaN(due.getTime()) && due < now;
    }).length;
  }

  get blockedTasks(): number {
    return this.tasks.filter((task) => String(task.status || '').toLowerCase() === 'blocked').length;
  }

  get documentCount(): number {
    const projectDocs = this.selectedProject?.mediaAttachments?.length || 0;
    const taskDocs = this.tasks.reduce((sum, task) => sum + (task?.mediaAttachments?.length || 0), 0);
    const commentDocs = this.tasks.reduce((sum, task) =>
      sum + (task?.comments || []).reduce((inner: number, comment: any) =>
        inner + this.countCommentMedia(comment), 0), 0);
    return projectDocs + taskDocs + commentDocs + this.onboarding.length;
  }

  get healthLabel(): string {
    if (!this.selectedProject) return 'No Project';
    if (this.overdueTasks > 0 || this.blockedTasks > 0) return 'At Risk';
    if (Number(this.selectedProject.progressPercent || 0) >= 85 && this.completedTasks >= Math.max(1, Math.round(this.tasks.length * 0.8))) return 'On Track';
    return 'Monitoring';
  }

  get healthClass(): string {
    return this.healthLabel.toLowerCase().replace(/\s+/g, '-');
  }

  get completionRate(): number {
    if (!this.tasks.length) return 0;
    return Math.round((this.completedTasks / this.tasks.length) * 100);
  }

  get upcomingItems(): any[] {
    return [...this.tasks]
      .filter((task) => !!task?.dueDate)
      .sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
      .slice(0, 5);
  }

  get recentActivity(): Array<{ label: string; when: string; type: string }> {
    const taskUpdates = this.tasks.map((task) => ({
      label: `Task updated: ${task.title}`,
      when: task.updatedAt,
      type: 'Task'
    }));
    const onboardingUpdates = this.onboarding.map((item) => ({
      label: `Document updated: ${item.title}`,
      when: item.updatedAt,
      type: 'Document'
    }));

    return [...taskUpdates, ...onboardingUpdates]
      .filter((item) => !!item.when)
      .sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime())
      .slice(0, 8);
  }

  countCommentMedia(comment: any): number {
    return (comment?.mediaAttachments?.length || 0)
      + (comment?.replies || []).reduce((sum: number, reply: any) => sum + (reply?.mediaAttachments?.length || 0), 0);
  }
}
